// Período de edição de uma venda: 1) associações explícitas salvas
// (venda_colheita_associacoes) → min/max das datas; 2) range legado (colheitaInicio/Fim, de
// vendas anteriores à associação granular); 3) a própria data da venda.
//
// Mora em shared/ porque o form de edição (client) e o cabeçalho do rateio (servidor,
// getRateioVenda) precisam do MESMO período — se divergirem, o período que o usuário vê e
// edita não é o período do número que ele está tentando corrigir.
// Ver Rizoma_Cacho_Vendas_FixEdicaoAssociacaoFantasma_Spec.md.
//
// ⚠️ Isto é RÓTULO DE TELA, não reivindicação. O range legado NUNCA reivindica um grupo no
// rateio (server/storage/rateio.ts): reivindicar um período inteiro destruiria a conservação
// Σ aloc = C(g) — a venda "levaria" todos os setores do range.

export interface VendaParaResolverPeriodo {
  data: string;
  colheitaInicio?: string | null;
  colheitaFim?: string | null;
  colheitaAssociacoes?: Array<{ data: string }> | null;
}

export function resolverPeriodoEdicaoVenda(venda: VendaParaResolverPeriodo): { inicio: string; fim: string } {
  const associacoes = venda.colheitaAssociacoes ?? [];
  if (associacoes.length > 0) {
    const datas = associacoes.map(a => a.data).sort();
    return { inicio: datas[0], fim: datas[datas.length - 1] };
  }
  if (venda.colheitaInicio || venda.colheitaFim) {
    return {
      inicio: venda.colheitaInicio ?? venda.colheitaFim!,
      fim: venda.colheitaFim ?? venda.colheitaInicio!,
    };
  }
  return { inicio: venda.data, fim: venda.data };
}

export type PresetPeriodoColheita = "hoje" | "ontem-hoje" | "custom";

/**
 * A janela de colheita que cada preset representa, a partir da data da venda.
 *
 * Mora aqui, pura, porque no form ela precisou deixar de ser um `useEffect` para virar ação
 * de clique — e essa é a correção do bug, não um detalhe de organização. Um efeito que deriva
 * estado de estado SEMPRE roda uma vez na montagem, com os valores do primeiro render; em
 * modo edição, esses valores iniciais são justamente os errados ("ontem + hoje" de hoje), e
 * ele sobrescrevia o período que acabara de ser carregado da venda salva. Como ação de
 * clique, a janela de corrida deixa de existir.
 *
 * `custom` devolve `null`: não há janela derivável — quem manda são os dois inputs de data.
 */
export function periodoDoPreset(
  preset: PresetPeriodoColheita,
  dataVenda: string,
): { inicio: string; fim: string } | null {
  if (preset === "hoje") return { inicio: dataVenda, fim: dataVenda };
  if (preset === "ontem-hoje") return { inicio: somarDias(dataVenda, -1), fim: dataVenda };
  return null;
}

/**
 * Soma dias a uma data ISO (YYYY-MM-DD) sem escorregar de fuso: o parse é ancorado em
 * meia-noite LOCAL e a formatação é manual, nunca via `toISOString()`.
 *
 * `toISOString()` não é seguro para data CIVIL — ele converte para UTC, e o dia resultante
 * depende do sinal do fuso:
 *   • fuso NEGATIVO (Bahia, UTC-3): meia-noite local = 03:00 UTC do MESMO dia → funciona;
 *   • fuso POSITIVO (Berlim, UTC+2): meia-noite local = 22:00 UTC do dia ANTERIOR → volta um dia.
 *
 * Ou seja: para uma data já ancorada em meia-noite, o erro depende do fuso, não da hora. Já
 * para `new Date()` (o instante de agora) o erro depende da hora — das 21:00 às 23:59 na
 * Bahia o UTC já virou o dia seguinte, que é o bug que trocou os `todayStr()` locais por
 * `hojeNaFazenda()` (client/src/lib/data-fazenda.ts). Dois modos de falhar, a mesma causa:
 * `toISOString()` responde em UTC, e o que a fazenda opera é data civil.
 */
export function somarDias(dataISO: string, dias: number): string {
  const d = new Date(dataISO + "T00:00:00");
  d.setDate(d.getDate() + dias);
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const dia = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mes}-${dia}`;
}
